import { supabase } from './supabase';
import { USE_MOCK_DATA, mockClientPlugins, mockPlugins } from './mockData';

export interface PluginConnection {
  pluginId: string;
  clientId: string;
  credentials: Record<string, string>;
  settings?: {
    syncFrequency?: 'realtime' | 'every_15_minutes' | 'every_30_minutes' | 'hourly' | 'every_4_hours' | 'daily' | 'weekly';
    autoSyncEnabled?: boolean;
    webhookUrl?: string;
  };
}

export interface SyncOptions {
  force?: boolean;
  syncType?: 'manual' | 'scheduled' | 'webhook';
}

export interface PluginHealth {
  pluginId: string;
  clientId: string;
  status: 'healthy' | 'degraded' | 'down';
  lastCheck: string;
  metrics: {
    totalSyncs: number;
    failedSyncs: number;
    successRate: number;
    avgSyncDuration: number;
  };
}

class PluginService {
  /**
   * Connect a plugin to a client
   */
  async connectPlugin(connection: PluginConnection) {
    if (USE_MOCK_DATA) {
      console.log('[PluginService] Mock mode: Would connect plugin', connection);
      return {
        success: true,
        data: {
          id: `mock_${connection.pluginId}_${connection.clientId}`,
          ...connection,
          is_connected: true,
          sync_status: 'active',
          created_at: new Date().toISOString()
        }
      };
    }

    try {
      // Insert the client plugin connection
      const { data, error } = await supabase
        .from('client_plugins')
        .insert({
          client_id: connection.clientId,
          plugin_id: connection.pluginId,
          credentials: connection.credentials,
          settings: connection.settings || {},
          is_connected: true,
          sync_status: 'active',
          sync_frequency: connection.settings?.syncFrequency || 'hourly',
          auto_sync_enabled: connection.settings?.autoSyncEnabled !== false
        })
        .select()
        .single();

      if (error) throw error;

      // Log the connection in audit logs
      await this.logAudit(data.id, 'plugin_connected', {
        plugin_id: connection.pluginId,
        client_id: connection.clientId
      });

      // Trigger initial sync
      await this.syncPlugin(data.id, { syncType: 'manual' });

      return { success: true, data };
    } catch (error: any) {
      console.error('[PluginService] Error connecting plugin:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Disconnect a plugin from a client
   */
  async disconnectPlugin(clientPluginId: string) {
    if (USE_MOCK_DATA) {
      console.log('[PluginService] Mock mode: Would disconnect plugin', clientPluginId);
      return { success: true };
    }

    try {
      // Log the disconnection
      await this.logAudit(clientPluginId, 'plugin_disconnected', {});

      // Update the connection status instead of deleting (for audit trail)
      const { error } = await supabase
        .from('client_plugins')
        .update({
          is_connected: false,
          sync_status: 'disconnected',
          auto_sync_enabled: false
        })
        .eq('id', clientPluginId);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('[PluginService] Error disconnecting plugin:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Reconnect a previously disconnected plugin
   */
  async reconnectPlugin(clientPluginId: string) {
    if (USE_MOCK_DATA) {
      console.log('[PluginService] Mock mode: Would reconnect plugin', clientPluginId);
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('client_plugins')
        .update({
          is_connected: true,
          sync_status: 'active',
          auto_sync_enabled: true,
          error_message: null
        })
        .eq('id', clientPluginId);

      if (error) throw error;

      await this.logAudit(clientPluginId, 'plugin_reconnected', {});
      await this.syncPlugin(clientPluginId, { syncType: 'manual' });

      return { success: true };
    } catch (error: any) {
      console.error('[PluginService] Error reconnecting plugin:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Sync a plugin
   */
  async syncPlugin(clientPluginId: string, options: SyncOptions = {}) {
    if (USE_MOCK_DATA) {
      console.log('[PluginService] Mock mode: Would sync plugin', clientPluginId, options);
      return {
        success: true,
        data: {
          id: `sync_${Date.now()}`,
          items_synced: Math.floor(Math.random() * 50) + 10,
          status: 'success',
          started_at: new Date().toISOString(),
          completed_at: new Date(Date.now() + 2000).toISOString()
        }
      };
    }

    const syncType = options.syncType || 'manual';
    const startedAt = new Date().toISOString();

    try {
      // Get client plugin details
      const { data: clientPlugin, error: fetchError } = await supabase
        .from('client_plugins')
        .select('*, plugin:plugins(*)')
        .eq('id', clientPluginId)
        .single();

      if (fetchError) throw fetchError;

      if (!clientPlugin.is_connected) {
        throw new Error('Plugin is not connected');
      }

      // Check rate limits
      const rateLimitOk = await this.checkRateLimit(clientPluginId);
      if (!rateLimitOk && !options.force) {
        throw new Error('Rate limit exceeded');
      }

      // Simulate sync operation (in production, this would call the actual API)
      const itemsSynced = await this.performSync(clientPlugin);

      const completedAt = new Date().toISOString();

      // Log successful sync
      const { data: syncLog, error: logError } = await supabase
        .from('plugin_sync_logs')
        .insert({
          client_plugin_id: clientPluginId,
          sync_type: syncType,
          status: 'success',
          items_synced: itemsSynced,
          started_at: startedAt,
          completed_at: completedAt
        })
        .select()
        .single();

      if (logError) throw logError;

      await this.logAudit(clientPluginId, 'plugin_synced', {
        items_synced: itemsSynced,
        sync_type: syncType
      });

      return { success: true, data: syncLog };
    } catch (error: any) {
      console.error('[PluginService] Sync error:', error);

      // Log failed sync
      await supabase.from('plugin_sync_logs').insert({
        client_plugin_id: clientPluginId,
        sync_type: syncType,
        status: 'error',
        items_synced: 0,
        error_details: { message: error.message, stack: error.stack },
        started_at: startedAt,
        completed_at: new Date().toISOString(),
        retry_after: new Date(Date.now() + 15 * 60 * 1000).toISOString() // Retry in 15 mins
      });

      return { success: false, error: error.message };
    }
  }

  /**
   * Perform the actual sync operation
   */
  private async performSync(clientPlugin: any): Promise<number> {
    // In production, this would:
    // 1. Use the credentials to authenticate with the platform
    // 2. Fetch new posts, analytics, comments, etc.
    // 3. Store them in the appropriate tables
    // 4. Return the count of items synced

    // For now, simulate a sync
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));

    // Return random count between 5 and 50
    return Math.floor(Math.random() * 46) + 5;
  }

  /**
   * Update plugin settings
   */
  async updatePluginSettings(clientPluginId: string, settings: any) {
    if (USE_MOCK_DATA) {
      console.log('[PluginService] Mock mode: Would update settings', clientPluginId, settings);
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('client_plugins')
        .update({
          settings,
          sync_frequency: settings.syncFrequency || 'hourly',
          auto_sync_enabled: settings.autoSyncEnabled !== false,
          updated_at: new Date().toISOString()
        })
        .eq('id', clientPluginId);

      if (error) throw error;

      await this.logAudit(clientPluginId, 'settings_updated', { settings });

      return { success: true };
    } catch (error: any) {
      console.error('[PluginService] Error updating settings:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update plugin credentials
   */
  async updatePluginCredentials(clientPluginId: string, credentials: Record<string, string>) {
    if (USE_MOCK_DATA) {
      console.log('[PluginService] Mock mode: Would update credentials', clientPluginId);
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('client_plugins')
        .update({
          credentials,
          updated_at: new Date().toISOString()
        })
        .eq('id', clientPluginId);

      if (error) throw error;

      await this.logAudit(clientPluginId, 'credentials_updated', {});

      // Test the new credentials with a sync
      await this.syncPlugin(clientPluginId, { syncType: 'manual' });

      return { success: true };
    } catch (error: any) {
      console.error('[PluginService] Error updating credentials:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get plugin health status
   */
  async getPluginHealth(clientPluginId: string): Promise<PluginHealth | null> {
    if (USE_MOCK_DATA) {
      return {
        pluginId: 'mock-plugin-id',
        clientId: 'mock-client-id',
        status: 'healthy',
        lastCheck: new Date().toISOString(),
        metrics: {
          totalSyncs: 142,
          failedSyncs: 3,
          successRate: 97.9,
          avgSyncDuration: 2.4
        }
      };
    }

    try {
      const { data: clientPlugin, error } = await supabase
        .from('client_plugins')
        .select('*')
        .eq('id', clientPluginId)
        .single();

      if (error) throw error;

      const successRate = clientPlugin.total_syncs > 0
        ? ((clientPlugin.total_syncs - clientPlugin.failed_syncs) / clientPlugin.total_syncs) * 100
        : 100;

      let status: 'healthy' | 'degraded' | 'down' = 'healthy';
      if (successRate < 50) status = 'down';
      else if (successRate < 90) status = 'degraded';

      return {
        pluginId: clientPlugin.plugin_id,
        clientId: clientPlugin.client_id,
        status,
        lastCheck: new Date().toISOString(),
        metrics: {
          totalSyncs: clientPlugin.total_syncs || 0,
          failedSyncs: clientPlugin.failed_syncs || 0,
          successRate: Math.round(successRate * 10) / 10,
          avgSyncDuration: 2.3 // Would calculate from sync logs in production
        }
      };
    } catch (error: any) {
      console.error('[PluginService] Error getting health:', error);
      return null;
    }
  }

  /**
   * Get sync history for a plugin
   */
  async getSyncHistory(clientPluginId: string, limit: number = 20) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: Array.from({ length: 5 }, (_, i) => ({
          id: `sync_${i}`,
          sync_type: i === 0 ? 'manual' : 'scheduled',
          status: i === 2 ? 'error' : 'success',
          items_synced: Math.floor(Math.random() * 50),
          started_at: new Date(Date.now() - i * 3600000).toISOString(),
          completed_at: new Date(Date.now() - i * 3600000 + 2000).toISOString()
        }))
      };
    }

    try {
      const { data, error } = await supabase
        .from('plugin_sync_logs')
        .select('*')
        .eq('client_plugin_id', clientPluginId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return { success: true, data };
    } catch (error: any) {
      console.error('[PluginService] Error getting sync history:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Check rate limit
   */
  private async checkRateLimit(clientPluginId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('plugin_rate_limits')
        .select('*')
        .eq('client_plugin_id', clientPluginId)
        .gte('window_start', new Date(Date.now() - 3600000).toISOString())
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (!data) {
        // Create new rate limit window
        await supabase.from('plugin_rate_limits').insert({
          client_plugin_id: clientPluginId,
          requests_made: 1,
          requests_limit: 100,
          reset_at: new Date(Date.now() + 3600000).toISOString()
        });
        return true;
      }

      if (data.requests_made >= data.requests_limit) {
        return false;
      }

      // Increment counter
      await supabase
        .from('plugin_rate_limits')
        .update({ requests_made: data.requests_made + 1 })
        .eq('id', data.id);

      return true;
    } catch (error) {
      console.error('[PluginService] Rate limit check error:', error);
      return true; // Allow on error
    }
  }

  /**
   * Log audit event
   */
  private async logAudit(clientPluginId: string, action: string, metadata: any) {
    try {
      await supabase.from('plugin_audit_logs').insert({
        client_plugin_id: clientPluginId,
        action,
        metadata,
        created_at: new Date().toISOString()
      });
    } catch (error) {
      console.error('[PluginService] Audit log error:', error);
    }
  }

  /**
   * Retry failed syncs
   */
  async retryFailedSyncs() {
    if (USE_MOCK_DATA) {
      console.log('[PluginService] Mock mode: Would retry failed syncs');
      return { success: true, retriedCount: 0 };
    }

    try {
      // Find failed syncs that are ready to retry
      const { data: failedSyncs, error } = await supabase
        .from('plugin_sync_logs')
        .select('client_plugin_id')
        .eq('status', 'error')
        .lte('retry_after', new Date().toISOString())
        .lt('retry_count', 3)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const uniquePlugins = [...new Set(failedSyncs?.map(s => s.client_plugin_id) || [])];

      for (const pluginId of uniquePlugins) {
        await this.syncPlugin(pluginId, { syncType: 'scheduled' });
      }

      return { success: true, retriedCount: uniquePlugins.length };
    } catch (error: any) {
      console.error('[PluginService] Error retrying syncs:', error);
      return { success: false, error: error.message, retriedCount: 0 };
    }
  }

  /**
   * Get plugins due for sync
   */
  async getPluginsDueForSync() {
    if (USE_MOCK_DATA) {
      return { success: true, data: [] };
    }

    try {
      const { data, error } = await supabase
        .from('client_plugins')
        .select('*, plugin:plugins(*), client:clients(company_name)')
        .eq('is_connected', true)
        .eq('auto_sync_enabled', true)
        .lte('next_sync_at', new Date().toISOString());

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[PluginService] Error getting plugins due for sync:', error);
      return { success: false, error: error.message, data: [] };
    }
  }
}

export const pluginService = new PluginService();
